import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import type { Offer } from '@psp-aggregator/sdk';

import {
  getOfferAttributes,
  getOfferDisplayName,
} from '../../libs/offerAttributes';
import { legMethodLabel } from '../../libs/offerDraft';
import { FIXED_FEE_ASSET, legSell } from '../../libs/offerRates';
import { OFFER_LEG_KEYS, OFFER_LEG_SHORT_I18N_KEY } from '../../model/legs';
import { GeoBadge } from '../GeoBadge/GeoBadge';

export interface CompactOfferRowProps {
  offer: Offer;
  pricingOnly?: boolean;
}

export const CompactOfferRow: FC<CompactOfferRowProps> = ({
  offer,
  pricingOnly = false,
}) => {
  const { t } = useTranslation();
  const attributes = getOfferAttributes(offer);
  const lines = OFFER_LEG_KEYS.flatMap((leg) => {
    const snapshot = attributes.legs[leg];

    if (!snapshot.enabled) {
      return [];
    }

    const sell = legSell(offer, leg);
    const extra = legMethodLabel(leg, snapshot);
    const sellText = snapshot.sell_fixed
      ? `${snapshot.sell_fixed} ${FIXED_FEE_ASSET}`
      : sell !== 0
        ? `${sell.toFixed(2)}%`
        : '';

    return [
      {
        extra,
        key: leg,
        label: t(OFFER_LEG_SHORT_I18N_KEY[leg]),
        sell: sellText,
      },
    ];
  });

  const pricing = (
    <div className="min-w-0">
      {lines.map((line) => (
        <p key={line.key} className="truncate text-[11px] leading-relaxed">
          <span className="text-muted-foreground">{line.label}:</span>
          {!!line.sell && (
            <span className="font-medium text-foreground"> {line.sell}</span>
          )}
          {!!line.extra && (
            <span className="text-muted-foreground"> · {line.extra}</span>
          )}
        </p>
      ))}
      {!!attributes.guarantee_amount && (
        <p className="truncate text-[11px] leading-relaxed">
          <span className="text-muted-foreground">
            {t('pages.admin.offers.fields.guarantee')}:
          </span>
          <span className="font-medium text-foreground">
            {' '}
            {attributes.guarantee_amount}
          </span>
        </p>
      )}
    </div>
  );

  if (pricingOnly) {
    return lines.length > 0 || attributes.guarantee_amount ? (
      pricing
    ) : (
      <span className="text-xs text-muted-foreground">—</span>
    );
  }

  return (
    <div className="min-w-0">
      <div className="flex min-w-0 items-center gap-2">
        <span className="truncate font-mono text-xs font-semibold">
          {offer.alias || getOfferDisplayName(offer)}
        </span>
        <GeoBadge
          countries={attributes.countries}
          regions={attributes.regions}
        />
      </div>
      {pricing}
    </div>
  );
};

CompactOfferRow.displayName = 'CompactOfferRow';
